import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { Analytics, LandingLayoutVariant } from '../analytics/client';
import { chooseLayout, layoutOverride } from '../../core/layouts';
import { frontDoorChoices, FRONT_DOOR_MIN_CHOICES, type FrontDoorAnswer, type FrontDoorVariant } from '../../core/frontDoor';
import FrontDoor from './FrontDoor';
import { listingsFromProps } from './layoutListings';
import PreviewEditorial from './PreviewEditorial';
import PreviewEditorialBench from './PreviewEditorialBench';
import PreviewLabBench from './PreviewLabBench';
import PreviewSchoolDay from './PreviewSchoolDay';
import type { PreviewLayoutProps } from './types';
import type { Listing } from '../../core/layouts';

/** How long an experiment boundary waits for its flag before falling back with no exposure. */
export const FLAG_TIMEOUT_MS = 1500;
type Assignment = { landing: LandingLayoutVariant | null; frontDoor: FrontDoorVariant | null };

export interface FrontDoorHandlers {
  studyHref: string;
  onFrontDoorAnswer: (answer: FrontDoorAnswer, details: { layout: string; tool?: string; text?: string }) => void;
  /** E17's strip, given the open listings so it can never stamp a hidden tool. */
  renderStampCard?: (listings: readonly Listing[]) => ReactNode;
}

/**
 * Only this boundary knows which layout and which front door are experiment arms (E10, E13).
 * The page waits for both, so the question never pops in under a page already drawn.
 */
export default function PreviewLayoutExperiment({ analytics, studyHref, onFrontDoorAnswer, renderStampCard, ...layout }: PreviewLayoutProps & FrontDoorHandlers & { analytics: Analytics | null }) {
  const [assignment, setAssignment] = useState<Assignment | null>(null);
  const settled = useRef(false);
  const exposed = useRef(false);

  useEffect(() => {
    if (settled.current) return;
    let live = true;
    let landing: LandingLayoutVariant | null | undefined;
    let frontDoor: FrontDoorVariant | null | undefined;
    const settle = () => {
      if (!live || settled.current) return;
      settled.current = true;
      setAssignment({ landing: landing ?? null, frontDoor: frontDoor ?? null });
    };
    const heard = () => { if (landing !== undefined && frontDoor !== undefined) settle(); };
    // A missing, failed or slow flag is today's page with no exposure for that experiment.
    const timeout = window.setTimeout(settle, FLAG_TIMEOUT_MS);
    const stops: (() => void)[] = [];
    if (analytics) {
      try {
        if (analytics.watchLandingLayoutVariant) stops.push(analytics.watchLandingLayoutVariant(v => { landing ??= v; heard(); }));
        else landing = null;
      } catch { landing = null; }
      try {
        if (analytics.watchFrontDoorVariant) stops.push(analytics.watchFrontDoorVariant(v => { frontDoor ??= v; heard(); }));
        else frontDoor = null;
      } catch { frontDoor = null; }
      heard();
    }
    return () => { live = false; window.clearTimeout(timeout); stops.forEach(stop => stop()); };
  }, [analytics]);

  useEffect(() => {
    if (!assignment || exposed.current || !analytics) return;
    exposed.current = true;
    // The chosen page has committed before this effect. A fallback is never exposure.
    try { if (assignment.landing) analytics.recordLandingLayoutExposure?.(assignment.landing); } catch { /* Keep the page usable. */ }
    try { if (assignment.frontDoor) analytics.recordFrontDoorExposure?.(assignment.frontDoor); } catch { /* Keep the page usable. */ }
  }, [analytics, assignment]);

  if (assignment === null) {
    return <main className="preview-loading" aria-busy="true"><p role="status">Loading 240 Labs…</p></main>;
  }
  const choice = chooseLayout(assignment.landing ?? layoutOverride(window.location.search, import.meta.env.PROD));
  const listings = listingsFromProps(layout);
  const choices = frontDoorChoices(listings);
  const stampCard = renderStampCard?.(listings) ?? null;
  const frontDoor = assignment.frontDoor === 'question' && choices.length >= FRONT_DOOR_MIN_CHOICES ? (aside?: ReactNode) => (
    <FrontDoor choices={choices} studyHref={studyHref} aside={aside}
      onAnswer={(answer, details) => onFrontDoorAnswer(answer, { layout: choice, ...details })} />
  ) : null;
  if (choice === 'school_day') return <PreviewSchoolDay {...layout} />;
  if (choice === 'lab_bench') return <PreviewLabBench {...layout} frontDoor={frontDoor} />;
  if (choice === 'editorial_bench') return <PreviewEditorialBench {...layout} frontDoor={frontDoor} stampCard={stampCard} />;
  return <PreviewEditorial {...layout} frontDoor={frontDoor} stampCard={stampCard} />;
}
